/**
 * Los filtros de la lista de Conversaciones: los 6 chips, la campaña y el buscador. No guarda nada: el
 * estado vive en la URL y lo maneja `ConversacionesLayout` (un enlace o un F5 muestran lo mismo).
 *
 * La ayuda va escrita debajo de los chips y no en un tooltip: «Por contestar», «Respondida» y «Resuelta»
 * se confunden, y un tooltip no se ve en el celular.
 *
 * Van a lo ancho de la página, arriba de la lista y de la conversación (auditoría de diseño del 18/10/2026): en la
 * columna de la lista (unos 440 px a 1366) los seis chips se partían en dos renglones y el buscador se cortaba en
 * «Buscar por número o non». Los chips van siempre en un renglón; donde no entran (el celular) se corren de costado,
 * y el borde que tiene más se difumina: antes, cortados contra el borde, nada decía que había «Resueltas» y «Bajas».
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Search } from "lucide-react";
import type { CampanaRef, FiltroConversaciones } from "../../types.conversaciones";
import { FILTROS } from "../../logic/conversaciones";

/** Qué bordes de la fila de chips esconden chips (se difuminan). */
export function bordesOcultos(m: { scrollLeft: number; clientWidth: number; scrollWidth: number }): { antes: boolean; despues: boolean } {
  return { antes: m.scrollLeft > 1, despues: m.scrollLeft + m.clientWidth < m.scrollWidth - 1 };
}

const DIFUMINADO = {
  antes: "linear-gradient(to right, transparent, black 24px)",
  despues: "linear-gradient(to right, black calc(100% - 24px), transparent)",
  ambos: "linear-gradient(to right, transparent, black 24px, black calc(100% - 24px), transparent)",
};

export function FiltrosConversaciones({ filtro, campana, campanas, texto, onFiltro, onCampana, onTexto }: {
  filtro: FiltroConversaciones;
  campana: string;
  campanas: CampanaRef[];
  texto: string;
  onFiltro: (f: FiltroConversaciones) => void;
  onCampana: (id: string) => void;
  onTexto: (t: string) => void;
}) {
  const idCampana = useId();
  // Una campaña de la URL que la página todavía no trajo se muestra igual: si no, el select mentiría «Todas».
  const campanaDesconocida = !!campana && !campanas.some((c) => c.id === campana);

  const fila = useRef<HTMLDivElement>(null);
  const [bordes, setBordes] = useState({ antes: false, despues: false });
  const medir = useCallback(() => {
    const el = fila.current;
    if (!el) return;
    const b = bordesOcultos(el);
    setBordes((prev) => (prev.antes === b.antes && prev.despues === b.despues ? prev : b));
  }, []);

  // El chip elegido, a la vista y centrado: con `?filtro=bajas` en el celular, «Bajas» quedaba fuera de la fila, y con los
  // de los dos lados asomando (difuminados) se ve que hay más para cada lado. Se mueve sólo la fila (no `scrollIntoView`,
  // que corre también la página). Se vuelve a ubicar cuando carga la fuente: antes los chips miden menos y el elegido
  // quedaba contra el borde.
  useEffect(() => {
    let vigente = true;
    const mostrarElegido = () => {
      const el = fila.current;
      const activo = el?.querySelector<HTMLElement>("[aria-pressed='true']");
      if (el && activo && el.scrollWidth > el.clientWidth) {
        el.scrollLeft = Math.max(0, activo.offsetLeft - (el.clientWidth - activo.offsetWidth) / 2);
      }
      medir();
    };
    mostrarElegido();
    void document.fonts?.ready.then(() => { if (vigente) mostrarElegido(); });
    return () => { vigente = false; };
  }, [filtro, medir]);

  useEffect(() => {
    window.addEventListener("resize", medir);
    return () => window.removeEventListener("resize", medir);
  }, [medir]);

  const mascara = bordes.antes && bordes.despues ? DIFUMINADO.ambos : bordes.antes ? DIFUMINADO.antes : bordes.despues ? DIFUMINADO.despues : undefined;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <div ref={fila} onScroll={medir} role="group" aria-label="Filtrar conversaciones"
          data-mas-antes={bordes.antes || undefined} data-mas-despues={bordes.despues || undefined}
          style={mascara ? { maskImage: mascara, WebkitMaskImage: mascara } : undefined}
          className="relative flex flex-nowrap items-center gap-1 overflow-x-auto scrollbar-hide rounded-xl bg-slate-100 p-1 w-fit max-w-full">
          {FILTROS.map((f) => (
            <button key={f.clave} type="button" onClick={() => onFiltro(f.clave)} aria-pressed={filtro === f.clave}
              className={`h-8 flex-shrink-0 px-3 rounded-lg text-xs font-semibold whitespace-nowrap ${filtro === f.clave ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              {f.label}
            </button>
          ))}
        </div>
        {/* La campaña y el buscador van juntos: al lado de los chips si entran, y si no en el renglón de abajo. En el
            celular, uno abajo del otro: lado a lado el buscador volvía a cortarse. */}
        <div data-testid="campana-y-buscador" className="flex flex-1 flex-col gap-2 sm:min-w-[28rem] sm:flex-row">
          <label htmlFor={idCampana} className="sr-only">Campaña</label>
          <select id={idCampana} value={campana} onChange={(e) => onCampana(e.target.value)}
            className="h-10 w-full sm:w-52 sm:flex-shrink-0 rounded-xl border border-slate-200 bg-white px-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500">
            <option value="">Todas las campañas</option>
            {campanas.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            {campanaDesconocida && <option value={campana}>Campaña elegida</option>}
          </select>
          <div className="relative min-w-0 flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={texto} maxLength={100} onChange={(e) => onTexto(e.target.value)}
              aria-label="Buscar por número o nombre" placeholder="Buscar por número o nombre"
              className="h-10 w-full rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
          </div>
        </div>
      </div>
      <p className="text-xs text-slate-500">
        {/* «Alguien del equipo» no se entendía del lado del cliente (¿su equipo o el de Deenex?): contesta cualquiera de los dos. */}
        Por contestar: escribieron hace menos de 24 h y nadie les contestó (arriba, la que vence primero). Respondida:
        ya le contestaron. Resuelta: no hace falta hacer nada más.
      </p>
    </div>
  );
}

export default FiltrosConversaciones;
